<script setup lang='ts'>
import OrganizationAutoComplete from "@/components/AccountParts/OrganizationAutoComplete.vue";
import NotificationSettings from '@/components/AccountParts/NotificationSettings.vue';
import { generatePassword, generateEmail, scrollToTop } from "@/services/functions";
import AccountNotificationSettingsType from '@/types/AccountNotificationSettings';
import DepartmentColumn from "@/components/AccountParts/DepartmentColumn.vue";
import AccountTextRow from "@/components/AccountParts/AccountTextRow.vue";
import PasswordForm from "@/components/AccountParts/PasswordForm.vue";
import TextFields from "@/components/AccountParts/TextFieldsRow.vue";
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi";
import { ref, Ref, onMounted, onBeforeUnmount, watch } from "vue";
import TextArea from "@/components/AccountParts/TextAreaRow.vue";
import ToggleRow from "@/components/AccountParts/ToggleRow.vue";
import InfoModal from '@/components/AccountParts/InfoModal.vue';
import { scrollToErrorComponent } from '@/services/validators';
import { initialAccount } from "@/services/functions/account";
import Select from "@/components/AccountParts/SelectRow.vue";
import { useAuth } from "@/services/user/Auth0UserProvider";
import { routingAuthorityNames } from '@/services/consts';
import getOfficesType from "@/types/responses/getOffices";
import FormRow from "@/components/Common/FormRow.vue";
import Api from "@/services/api/ApiServiceFabrick";
import SelfAccountType from "@/types/SelfAccount";
import { useRoute, useRouter } from "vue-router";
import "gitart-vue-dialog/dist/style.css";
import accountType from "@/types/account";
import {
  MANAGER_ROLE_ID,
  DRIVER_ROLE_ID,
  MANAGEMENT_OFFICE,
  SUB_MANAGEMENT_OFFICE,
  INSTALLATION_OFFICE,
  OPERATION_OFFICE,
  DEPARTMENT,
  DEFAULT_SELECTED_OFFICE,
  BLANK_ROLE,
  ROLES,
  INVALID_ACCOUNT_PROPS,
  BLANK_OFFICEABLE_TYPE,
} from "@/services/consts/account";
import i18n from "@/lang/i18n";

const props = defineProps<{
  showFlash: (message: string, type: string, fadeout: boolean) => void
  switchContentLoading: (isLoading: boolean) => void
}>()

type officeType = {
  id: number | null
  type: string
  name: string | undefined
  departments: getOfficesType['departments']
}

type officeableType = {
  id: number | null
  name: string | null
  type: string | null
}

const router = useRouter();
const route = useRoute();
const accountId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id ?? null
const account: Ref<accountType> = ref(initialAccount())
const errors: Ref<{[key: string]: string[]}> = ref({})
const officeables: Ref<officeType[]> = ref([])
const beforeEmail: Ref<string> = ref("")
const modalState: Ref<boolean> = ref(false)
const isCreatingOrUpdating: Ref<boolean> = ref(false)
const selectedDepartment: Ref<{
  id: number | null,
  operation_office_id: number | null,
  name: string | null
}> = ref({ id: null, operation_office_id: null, name: null })
const selectedRole: Ref<{ name: string, value: number | null }> = ref(BLANK_ROLE)
const initialDepartmentId: Ref<number | null> = ref(null)
const createAndUpdateAuthority: Ref<boolean> = ref(false)
const selectedOfficeableType: Ref<officeableType> = ref(BLANK_OFFICEABLE_TYPE)
const selectedOffice: Ref<officeType> = ref(DEFAULT_SELECTED_OFFICE)
const selfAccountOfficeableType: Ref<string> = ref("")
const showableOfficeableTypes: Ref<any[]> = ref([])
const disableWatchOfficeableType: Ref<boolean> = ref(false)
const disableWatchRole: Ref<boolean> = ref(false)
const roleAssignAuthority: Ref<boolean> = ref(false)
const temperatureAuthority: Ref<boolean> = ref(false)
const outputDataAuthority: Ref<boolean> = ref(false)
const notificationSettings: Ref<AccountNotificationSettingsType> = ref({
  riskyDriving: {
    rapid_acceleration: true,
    rapid_deceleration: true,
    steep_turn: true,
  },
  cvrUpload: {
    completed: true,
  },
  temperature: {
    sensor_disabled: true,
    low_battery: true,
  },
  outputData: {
    completed: true,
  },
})
const { loginWithRedirect } = useAuth();

onMounted( async() => {
  props.switchContentLoading(true);
  scrollToTop()

  await getAuthorities()
  checkCreateAuthority()

  await Promise.all([
    getSelfAccount(),
    getOffices(),
  ]).finally(() => {
    if (!accountId) {
      setNotificationSettings(account.value.account_notification_setting)
      return props.switchContentLoading(false);
    }
  })

  if (accountId) {
    await getAccount(Number(accountId));
  }
})

function title() {
  return accountId ? i18n.global.t('pages.accounts.title.edit') : i18n.global.t('pages.accounts.title.create');
}

function isManagementOffice() {
  return selfAccountOfficeableType.value == MANAGEMENT_OFFICE.type
}

function isSubManagementOffice() {
  return selfAccountOfficeableType.value == SUB_MANAGEMENT_OFFICE.type
}

function isOperationOffice() {
  return selfAccountOfficeableType.value == OPERATION_OFFICE.type
}

function isSelectedManagementOffice() {
  return selectedOfficeableType.value.type == MANAGEMENT_OFFICE.type
}
function isSelectedSubManagementOffice() {
  return selectedOfficeableType.value.type == SUB_MANAGEMENT_OFFICE.type
}
function isSelectedOperationOffice() {
  return selectedOfficeableType.value.type == OPERATION_OFFICE.type
}

function isSelectedManager() {
  return !!(
    (isSelectedOperationOffice() && selectedRole.value && selectedRole.value['value'] == MANAGER_ROLE_ID) ||
    isSelectedManagementOffice() ||
    isSelectedSubManagementOffice()
  )
}

function isSelectedDriver() {
  return !!(isSelectedOperationOffice() && selectedRole.value && selectedRole.value['value'] == DRIVER_ROLE_ID)
}

function isDriverMode() {
  return isSelectedDriver() && account.value.enabled_driver_mode
}

async function getAuthorities() {
  const authorityList = await AuthorityCacheApi.getWithParse();
  if (accountId) {
    createAndUpdateAuthority.value = !!authorityList["octlink.user.update"];
  } else {
    createAndUpdateAuthority.value = !!authorityList["octlink.user.create"];
  }
  roleAssignAuthority.value = !!authorityList["octlink.role_attach.update"];
  temperatureAuthority.value = !!authorityList[routingAuthorityNames.Temperature];
  outputDataAuthority.value = !!authorityList[routingAuthorityNames.DownloadData];
}

function checkCreateAuthority() {
  if (!accountId && !createAndUpdateAuthority.value) {
    props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", false);
    router.push("/accounts");
    return;
  }
}

async function getSelfAccount() {
  await Api().getSelfAccount().then((data: SelfAccountType) => {
    selfAccountOfficeableType.value = data.officeable_type.split("\\")[2]
    const roleId = data.role_id;
    if (roleId == DRIVER_ROLE_ID) {
      return driverAcction();
    }

    setShowableOfficeableType()
  })
}

function driverAcction() {
  props.switchContentLoading(false);
  props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", false);
  router.push("/accounts");
}

async function getOffices() {
  await Api().getOffices().then((data: getOfficesType[]) => {
    officeables.value = data.map((office) => {
      return {
        id: office.id,
        type: office.type.split("\\")[2],
        name: office.company_name ?? office.operation_office_name,
        departments: office.departments ?? [],
      }
    });
  });
}

async function getAccount(accountId: number) {
  disableWatchOfficeableType.value = true;
  disableWatchRole.value = true;

  Api().getAccount(accountId).then((data: accountType) => {
    account.value = data;
    beforeEmail.value = data.email
    account.value.no_email = data.temporary_email?.length ? true : false;
    initialDepartmentId.value = data.department_id

    setSelectedOfficeableType(data);
    setSelectedRole(data)
    if (
      (data.officeable_type.split("\\")[2] == OPERATION_OFFICE.type && data.role_id == MANAGER_ROLE_ID) ||
      (data.officeable_type.split("\\")[2] == MANAGEMENT_OFFICE.type) ||
      (data.officeable_type.split("\\")[2] == SUB_MANAGEMENT_OFFICE.type)
    ) {
      setNotificationSettings(data.account_notification_setting)
    } else {
      setNotificationSettings(initialAccount().account_notification_setting)
    }
    resetOffices(selectedOfficeableType.value, data.officeable_id);
  }).catch((error) => {
    const res = error.response;
    if (res.status == 403) {
      props.showFlash(i18n.global.t('pages.common.messages.notEditAuthority'), "error", true);
      router.push("/accounts/");
    }
  }).finally(() => {
    disableWatchRole.value = false;
    props.switchContentLoading(false);
  });
}

function setSelectedOfficeableType(data: accountType) {
  switch (data.officeable_type.split("\\")[2]) {
    case MANAGEMENT_OFFICE.type:
      return selectedOfficeableType.value = MANAGEMENT_OFFICE;
    case SUB_MANAGEMENT_OFFICE.type:
      return selectedOfficeableType.value = SUB_MANAGEMENT_OFFICE;
    case OPERATION_OFFICE.type:
      return selectedOfficeableType.value = OPERATION_OFFICE;
    default:
      return selectedOfficeableType.value = INSTALLATION_OFFICE;
  }
}

function setSelectedRole(data: accountType) {
  if (data.officeable_type.split("\\")[2] == OPERATION_OFFICE.type) {
    selectedRole.value = ROLES.find((role) => {
      return role.value == data.role_id
    }) ?? BLANK_ROLE;
  }
}

function setNotificationSettings(data: accountType['account_notification_setting']) {
  const riskyDriving = data.risky_driving_alerts;
  const cvrUpload = data.cvr_upload_alerts;
  const temperature = data.temperature_alerts;
  const outputData = data.output_data_alerts;

  notificationSettings.value = {
    riskyDriving: {
      rapid_acceleration: riskyDriving.rapid_acceleration,
      rapid_deceleration: riskyDriving.rapid_deceleration,
      steep_turn: riskyDriving.steep_turn,
    },
    cvrUpload: {
      completed: cvrUpload.completed,
    },
    temperature: {
      sensor_disabled: temperature.sensor_disabled,
      low_battery: temperature.low_battery,
    },
    outputData: {
      completed: outputData.completed,
    },
  }
}

function setShowableOfficeableType() {
  switch (selfAccountOfficeableType.value) {
    case MANAGEMENT_OFFICE.type:
      showableOfficeableTypes.value = [
        MANAGEMENT_OFFICE,
        SUB_MANAGEMENT_OFFICE,
        INSTALLATION_OFFICE,
        OPERATION_OFFICE,
      ];
      break;
    case SUB_MANAGEMENT_OFFICE.type:
      showableOfficeableTypes.value = [
        SUB_MANAGEMENT_OFFICE,
        INSTALLATION_OFFICE,
        OPERATION_OFFICE,
      ];
      break;
    case OPERATION_OFFICE.type:
    case DEPARTMENT.type:
      showableOfficeableTypes.value = [OPERATION_OFFICE];
      break;
    default:
      showableOfficeableTypes.value = [];
  }

  if (showableOfficeableTypes.value.length) {
    showableOfficeableTypes.value.unshift(BLANK_OFFICEABLE_TYPE);
  }
}

function resetOffices(officeableType: officeableType, officeableId: number | null = null) {
  if (!officeableType.type || !officeableId) {
    return selectedOffice.value = DEFAULT_SELECTED_OFFICE;
  }

  let filteredOffices: officeType[] = []
  filteredOffices = officeables.value.filter((office) => {
    return office.type == officeableType.type
  });

  selectedOffice.value = filteredOffices.find((office: officeType) => {
    return office.id == officeableId
  }) ?? DEFAULT_SELECTED_OFFICE
}

function makeMailAdress() {
  if (!account.value.email && selectedOffice.value?.id) {
    account.value.email = generateEmail(account.value.driver_id, selectedOffice.value.id)
  }
}

function checkEmailAndPassword(bool: boolean) {
  if (!isDriverMode()) {
    return false
  }

  if (!bool) {
    return bool
  }

  const diffEmail = beforeEmail.value != account.value.email
  const existsName = !!account.value.last_name && !!account.value.first_name
  const existsOfficeAndDriverId = !!account.value.driver_id && !!selectedOffice.value?.id
  const isNecessaryPassword = passwordConditions()

  return (diffEmail || !account.value.email || isNecessaryPassword)
    && existsName
    && existsOfficeAndDriverId
}

function openAnnounceRoleAssignModal() {
  makeMailAdress()
  makePassword()
  modalState.value = true
}

function makePassword() {
  if (!accountId && !account.value.password) {
    account.value.password = account.value.driver_id ? generatePassword(account.value.driver_id) : ""
  }
}

function passwordConditions() {
  if (accountId) {
    return !!account.value.password
  }
  return !account.value.password && account.value.driver_id
}

function createOrUpdate(bool = true) {
  errors.value = {}
  if (accountId && checkEmailAndPassword(bool)) {
    return openAnnounceRoleAssignModal();
  }

  isCreatingOrUpdating.value = true;
  const apiRequetFunc = accountId ? Api().updateAccount : Api().createAccount;

  apiRequetFunc(makeParams()).then((data) => {
    if (!accountId) {
      return openAnnounceRoleAssignModal()
    }
    props.showFlash(i18n.global.t('pages.common.messages.success'), "success", true);
    router.push("/accounts");
  }).catch((error) => {
    const res = error.response;
    switch (res.status) {
      case 401:
        loginWithRedirect({
          appState: { targetUrl: location.pathname },
        });
        break;
      case 403:
        location.reload();
        break;
      case 422:
        errors.value = res.data.errors;
        scrollToErrorComponent();
    }
  }).finally(() => {
    isCreatingOrUpdating.value = false;
  });
}

function makeParams() {
  const params = JSON.parse(JSON.stringify(account.value))
  params["no_email"] = params["no_email"] ? 1 : 0;
  params["officeable_type"] = selectedOfficeableType.value.id;
  params["officeable_id"] = selectedOffice.value?.id;
  params['role_id'] = makeRoleId()
  if (params["role_id"] != DRIVER_ROLE_ID) {
    params["driver_id"] = null;
    params["enabled_driver_mode"] = false
  }
  if (isDriverMode()) {
    params["no_email"] = 0;
  }

  params["department_id"] = selectedDepartment.value?.id ?? null;

  INVALID_ACCOUNT_PROPS.forEach((prop: string) => {
    delete params[prop]
  });

  if (!params["enabled_driver_mode"]) {
    delete params["password"];
  }

  if (isSelectedManager()) {
    params["account_notification_setting"] = makeAccountNotificationSettingParams();
  }

  return params;
}

function makeAccountNotificationSettingParams() {
  let temperatureAlerts = {
    sensor_disabled: false,
    low_battery: false,
  }
  if (temperatureAuthority.value) {
    temperatureAlerts = notificationSettings.value.temperature
  }

  let outputDataAlerts = {
    completed: false,
  }
  if (outputDataAuthority.value) {
    outputDataAlerts = notificationSettings.value.outputData
  }

  return {
    risky_driving_alerts: notificationSettings.value.riskyDriving,
    cvr_upload_alerts: notificationSettings.value.cvrUpload,
    temperature_alerts: temperatureAlerts,
    output_data_alerts: outputDataAlerts,
  }
}

function makeRoleId() {
  return isSelectedOperationOffice() ? selectedRole.value['value'] : MANAGER_ROLE_ID;
}

function isEmailRequired() {
  return isOperationOffice() && selectedRole.value['value'] == MANAGER_ROLE_ID
}

watch(() => selectedOfficeableType.value, (value) => {
  if (value.type != OPERATION_OFFICE.type) {
    selectedRole.value = BLANK_ROLE;
  }
  if (disableWatchOfficeableType.value) {
    return disableWatchOfficeableType.value = false;
  }
  resetOffices(value);
})

watch(() => account.value.enabled_driver_mode, (value) => {
  if (value && account.value.driver_id && selectedOffice.value?.id) {
    makeMailAdress()
  }
})

watch(() => selectedRole.value, (value) => {
  if (value && !disableWatchRole.value) {
    account.value.enabled_driver_mode = true
  }
})

onBeforeUnmount(() => {
  props.switchContentLoading(false);
})
</script>
<template>
  <div class="d-flex" style="min-height: 45vw">
    <div class="content p-4 w-100">
      <h4 class="card-title mt-0">{{ title() }}</h4>
      <div class="row mr-3 v-theme--light">
        <div class="mb-4">
          <Select
            :title="i18n.global.t('pages.accounts.accountCategory')"
            :required="true"
            v-model="selectedOfficeableType"
            itemTitle="name"
            itemValue="id"
            :disabled="isDriverMode()"
            :items="showableOfficeableTypes"
            :errors="errors['officeable_type']"
          />
          <FormRow
            :title="i18n.global.t('pages.accounts.officeableName')"
            :required="true"
          >
            <OrganizationAutoComplete
              label=""
              v-model="selectedOffice"
              :selectedOfficeableType="selectedOfficeableType"
              :clearable="false"
              :errorMessages="errors['officeable_id'] ?? []"
            />
          </FormRow>
          <FormRow
            v-if="isSelectedOperationOffice()"
            :title="i18n.global.t('pages.accounts.role')"
            :required="true"
          >
            <v-radio-group
              v-model="selectedRole"
              :error-messages="errors['role_id']"
              inline
              class="role-radio-group pt-2"
              color="primary"
            >
              <v-radio
                v-for="role in ROLES"
                :key="role.value"
                :label="role.name"
                :value="role"
              />
            </v-radio-group>
          </FormRow>
          <TextFields
            :modelValue="account"
            :title="i18n.global.t('pages.accounts.form.name')"
            :labels="[
              i18n.global.t('pages.accounts.form.lastName'),
              i18n.global.t('pages.accounts.form.firstName')
            ]"
            :errors="errors"
            :itemKeys="['last_name', 'first_name']"
            :required="true"
          />
          <TextFields
            :title="i18n.global.t('pages.accounts.form.nameKana')"
            :labels="[
              i18n.global.t('pages.accounts.form.lastNameKana'),
              i18n.global.t('pages.accounts.form.firstNameKana')
            ]"
            :errors="errors"
            v-model="account"
            :itemKeys="['last_name_kana', 'first_name_kana']"
            :required="true"
          />
          <AccountTextRow
            v-if="isSelectedDriver()"
            :title="i18n.global.t('pages.accounts.driverId')"
            :errors="errors['driver_id'] ?? []"
            v-model="account.driver_id"
            :required="true"
          />
          <ToggleRow
            v-if="isSelectedDriver()"
            :title="i18n.global.t('pages.accounts.form.driverMode')"
            v-model="account.enabled_driver_mode"
          />
          <AccountTextRow
            v-if="!account.no_email || isSelectedDriver()"
            :title="i18n.global.t('pages.accounts.form.emailAddress')"
            :errors="errors['email'] ?? []"
            v-model="account.email"
            :generateButtonName="i18n.global.t('pages.accounts.form.automaticIssuanceEmailAddress')"
            :isGenerate="isDriverMode()"
            :driverId="account.driver_id"
            :officeId="selectedOffice?.id ? selectedOffice.id : null"
            :required="isEmailRequired()"
          />
          <ToggleRow
            v-if="(isManagementOffice() || isSubManagementOffice()) && !isSelectedDriver()"
            :title="i18n.global.t('pages.accounts.form.NoEmailAddress')"
            v-model="account.no_email"
            :disabled="isDriverMode()"
          />
          <AccountTextRow
            v-if="account.no_email && !isSelectedDriver()"
            :title="i18n.global.t('pages.accounts.form.tempEmailAddress')"
            :errors="errors['temporary_email'] ?? []"
            v-model="account.temporary_email"
            :required="true"
          />
          <PasswordForm
            v-if="isDriverMode()"
            :title="i18n.global.t('pages.accounts.form.password')"
            :errors="errors['password'] ?? []"
            :accountId="accountId"
            :isDriverMode="isDriverMode()"
            :driverId="account.driver_id"
            v-model="account.password"
          />
          <ToggleRow
            v-if="(isManagementOffice() || isSubManagementOffice()) && isSelectedDriver()"
            :title="i18n.global.t('pages.accounts.form.operationVerificationAccount')"
            v-model="account.enabled_factory_setting_account"
          />
          <FormRow
            v-if="isSelectedManager()"
            :title="i18n.global.t('pages.accounts.form.notificationSettings.title')"
            :required="false"
          >
            <NotificationSettings
              :modelValue="notificationSettings"
              :temperatureAuthority="temperatureAuthority"
              :outputDataAuthority="outputDataAuthority"
            />
          </FormRow>
          <DepartmentColumn
            v-model="selectedDepartment"
            :selectedOfficeableType="selectedOfficeableType"
            :selectedOffice="selectedOffice"
            :initialItemId="initialDepartmentId"
            :errors="errors['department_id'] ?? []"
            :isDepartment="false"
          />
          <AccountTextRow
            :title="i18n.global.t('pages.accounts.telephoneNumber')"
            :errors="errors['telephone_number'] ?? []"
            v-model="account.telephone_number"
          />
          <TextArea
            :title="i18n.global.t('pages.accounts.form.comment')"
            :errors="errors['comment']"
            v-model="account.comment"
          />
        </div>
        <div
          class="form-footer p-2"
        >
          <router-link :to="{ name: 'Accounts' }">
              <v-btn class="ma-2 reset-btn">
                {{ i18n.global.t('common.cancelBtn') }}
              </v-btn>
          </router-link>
          <v-btn
            v-if="createAndUpdateAuthority"
            class="ma-2 exec-btn"
            :loading="isCreatingOrUpdating"
            @click="createOrUpdate()"
          >
            {{ i18n.global.t('pages.common.button.conservation') }}
          </v-btn>
        </div>
      </div>
      <InfoModal
        v-model="modalState"
        :isDriverMode="isDriverMode()"
        :accountId="accountId"
        :modalContent="{
          email: account.email,
          password: account.password,
          name: [account.last_name, account.first_name]
        }"
        :isLoading="isCreatingOrUpdating"
        :update="createOrUpdate"
        :roleAssignAuthority="roleAssignAuthority"
      />
    </div>
  </div>
</template>
<style>
.modal-header {
  font-weight: 600;
}
</style>
<style lang="scss">
.custom-rotate-transition {
  &-enter-from {
    transform: translate(0, 30px) rotate(20deg);
    opacity: 0;
  }

  &-leave-to {
    transform: translate(0, 30px) rotate(10deg);
    opacity: 0;
  }
}

.custom-from-bottom-transition {
  &-enter-from {
    transform: translate(0, 100%);
    opacity: 0;
  }

  &-leave-to {
    transform: translate(0, -30%);
    opacity: 0;
  }
}
.error *,
.v-input--error .v-messages__message {
  color: rgb(var(--v-theme-error));
  opacity: 1;
}
.role-radio-group .v-label {
  opacity: 1;
}
</style>
